import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertTriangle, Edit, ArrowLeft, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCPF } from "@/lib/cpf-validator";
import { toast } from "sonner";

interface MappedPatient {
  full_name: string;
  cpf: string;
  data_nascimento?: string | null;
  sexo?: string | null;
  email?: string | null;
  celular?: string | null;
  confidence: {
    full_name: number;
    cpf: number;
    data_nascimento: number;
    sexo: number;
    email: number;
    celular: number;
  };
}

interface ImportMappingReviewProps {
  patients: MappedPatient[];
  overallConfidence: number;
  warnings: string[];
  onConfirm: (patients: MappedPatient[]) => void;
  onBack: () => void;
}

export function ImportMappingReview({
  patients,
  overallConfidence,
  warnings,
  onConfirm,
  onBack
}: ImportMappingReviewProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedPatients, setEditedPatients] = useState<MappedPatient[]>(patients);

  useEffect(() => {
    setEditedPatients(patients);
  }, [patients]);

  const updatePatient = (idx: number, field: keyof MappedPatient, value: any) => {
    const updated = [...editedPatients];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditedPatients(updated);
  };

  const saveEdit = (idx: number) => {
    setEditingIndex(null);
    toast.success("Dados atualizados");
  };

  const cancelEdit = () => {
    setEditedPatients(patients);
    setEditingIndex(null);
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 90) {
      return <Badge className="bg-green-500 hover:bg-green-600">✓ {score}%</Badge>;
    } else if (score >= 70) {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">⚠ {score}%</Badge>;
    } else {
      return <Badge variant="destructive">✗ {score}%</Badge>;
    }
  };

  const getFieldClassName = (score: number) => {
    if (score >= 90) return "text-foreground";
    if (score >= 70) return "text-yellow-600 dark:text-yellow-400";
    return "text-destructive";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Revisar Mapeamento</h2>
          <p className="text-muted-foreground">
            Verifique os dados detectados pela IA
          </p>
        </div>
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
      </div>

      {/* Overall Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Pacientes Detectados</CardDescription>
            <CardTitle className="text-3xl">{patients.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Confiança Geral</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              {overallConfidence}%
              {overallConfidence >= 90 ? (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-yellow-500" />
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Avisos</CardDescription>
            <CardTitle className="text-3xl">{warnings.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-2">Avisos detectados:</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {warnings.slice(0, 5).map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
              {warnings.length > 5 && (
                <li className="text-muted-foreground">
                  + {warnings.length - 5} avisos adicionais
                </li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      <Card>
        <CardHeader>
          <CardTitle>Preview dos Dados</CardTitle>
          <CardDescription>
            Cores indicam confiança: <span className="text-green-500">verde ≥90%</span>,
            <span className="text-yellow-500 mx-1">amarelo 70-89%</span>,
            <span className="text-destructive">vermelho &lt;70%</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Data Nasc.</TableHead>
                  <TableHead>Sexo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Celular</TableHead>
                  <TableHead className="w-24">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editedPatients.map((patient, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-muted-foreground">
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          value={patient.full_name}
                          onChange={(e) => updatePatient(idx, "full_name", e.target.value)}
                          className="min-w-[200px]"
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={getFieldClassName(patient.confidence.full_name)}>
                            {patient.full_name || "—"}
                          </div>
                          {getConfidenceBadge(patient.confidence.full_name)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          value={patient.cpf}
                          onChange={(e) => updatePatient(idx, "cpf", e.target.value)}
                          className="min-w-[150px] font-mono"
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={cn("font-mono", getFieldClassName(patient.confidence.cpf))}>
                            {patient.cpf ? formatCPF(patient.cpf) : "—"}
                          </div>
                          {getConfidenceBadge(patient.confidence.cpf)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          type="date"
                          value={patient.data_nascimento || ""}
                          onChange={(e) => updatePatient(idx, "data_nascimento", e.target.value)}
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={getFieldClassName(patient.confidence.data_nascimento)}>
                            {patient.data_nascimento || "—"}
                          </div>
                          {patient.data_nascimento && getConfidenceBadge(patient.confidence.data_nascimento)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          value={patient.sexo || ""}
                          onChange={(e) => updatePatient(idx, "sexo", e.target.value)}
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={getFieldClassName(patient.confidence.sexo)}>
                            {patient.sexo || "—"}
                          </div>
                          {patient.sexo && getConfidenceBadge(patient.confidence.sexo)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          type="email"
                          value={patient.email || ""}
                          onChange={(e) => updatePatient(idx, "email", e.target.value)}
                          className="min-w-[200px]"
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={cn("text-sm", getFieldClassName(patient.confidence.email))}>
                            {patient.email || "—"}
                          </div>
                          {patient.email && getConfidenceBadge(patient.confidence.email)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <Input
                          value={patient.celular || ""}
                          onChange={(e) => updatePatient(idx, "celular", e.target.value)}
                          className="min-w-[150px]"
                        />
                      ) : (
                        <div className="space-y-1">
                          <div className={cn("font-mono", getFieldClassName(patient.confidence.celular))}>
                            {patient.celular || "—"}
                          </div>
                          {patient.celular && getConfidenceBadge(patient.confidence.celular)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingIndex === idx ? (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => saveEdit(idx)}>
                            <Check className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => cancelEdit()}>
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="icon" variant="ghost" onClick={() => setEditingIndex(idx)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={() => onConfirm(editedPatients)}
          size="lg"
          disabled={editedPatients.length === 0}
          className="gap-2"
        >
          <CheckCircle2 className="h-4 w-4" />
          Confirmar e Importar {editedPatients.length} Paciente{editedPatients.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}
